import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { round } from '@mahawi/eshop-common/src/round';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  getLowerCaseLanguageCode,
  getTranslation,
} from '@mahawi/eshop-common/src/translation';
import {
  type IAdminCategory,
  type IComparisonPortal,
  type IEbayMarketplace,
} from '@mahawi/eshop-common/src/types';
import {
  Button,
  Col,
  Collapse,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { Key, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import WarehouseCategorySuggestions from '../components/category/warehouse-category-suggestions';
import ExternalLink from '../components/external-link';
import LanguageButtons from '../components/language-buttons';
import Link from '../components/link';
import { type ISortableListItem } from '../components/sortable-list';
import { SortableList } from '../components/sortable-list';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { categoriesLoadAll, categoryAdd } from '../reducers/categories/reducer';
import { categorySavePosition } from '../reducers/category/reducer';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;

interface IForm {
  name: string;
}

interface DataType extends Omit<
  IAdminCategory,
  | 'names'
  | 'property'
  | 'slugs'
  | 'descriptions'
  | 'metaDescriptions'
  | 'metaKeywords'
> {
  key: string;
  name: string;
}

function ItemTemplate({
  record,
}: {
  record: ISortableListItem;
}): React.ReactElement {
  return <div>{record.name}</div>;
}

function CategoriesContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const categoriesAll = useSelector((s: RootState) => s.Categories.categories);
  const categoriesInProcess = useSelector(
    (s: RootState) => s.Categories.inProcess,
  );
  const categoriesUpdatedAt = useSelector(
    (s: RootState) => s.Categories.updatedAt,
  );
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const configUrl = useSelector((s: RootState) => s.Config.url);

  const [form] = Form.useForm<IForm>();

  const onFinish = useCallback(
    ({ name }: IForm): void => {
      if (!languageType) {
        return;
      }

      dispatch(categoryAdd(name, languageType.code));
    },
    [dispatch, languageType?.code],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showOnlyParentCategories, setShowOnlyParentCategories] =
    useState(true);

  const dataSource = useMemo<DataType[]>(
    () =>
      (categoriesAll ?? [])
        .map((category: IAdminCategory): DataType | null => {
          if (category.parentCategoryUUID && showOnlyParentCategories) {
            return null;
          }

          const nameLocalized: string | null = getTranslation(
            category.names,
            languageType,
          );

          return {
            key: category.uuid,
            uuid: category.uuid,
            name: nameLocalized || EMPTY_VALUE,
            childCategoriesUUIDs: category.childCategoriesUUIDs,
            position: category.position,
            comparisonPortalTypes: category.comparisonPortalTypes,
            ebay: category.ebay,
            parentCategoryUUID: category.parentCategoryUUID,
            endPriceMarginRate: category.endPriceMarginRate,
          };
        })
        .filter(notEmpty),
    [categoriesAll, languageType, showOnlyParentCategories],
  );

  const dataSourceSortableList = useMemo<ISortableListItem[]>(
    () =>
      dataSource.map(({ key, name, childCategoriesUUIDs, position }) => ({
        id: key,
        uuid: key,
        name: `${name} (${childCategoriesUUIDs.length})`,
        position,
      })),
    [dataSource],
  );

  const columns = useMemo<ColumnsType>(() => {
    const parentOrChildColumn = showOnlyParentCategories
      ? {
          title: 'Child categories count',
          dataIndex: 'childCategoriesUUIDs',
          render: (text: string[]) => text.length,
          sorter: (a: DataType, b: DataType): number =>
            a.childCategoriesUUIDs.length - b.childCategoriesUUIDs.length,
          width: '10%',
        }
      : {
          title: 'Parent category',
          dataIndex: 'parentCategoryUUID',
          render: (text: string): React.ReactElement | string => {
            if (!text) {
              return 'PARENT';
            }

            const parentCategory: IAdminCategory | undefined =
              categoriesAll?.find(
                (category: IAdminCategory): boolean => category.uuid === text,
              );

            if (!parentCategory) {
              return EMPTY_VALUE;
            }

            const nameLocalized: string | null = getTranslation(
              parentCategory.names,
              languageType,
            );

            return (
              <Link
                to={`/category/${parentCategory.uuid}`}
                label={nameLocalized || EMPTY_VALUE}
                openInNewTab
              />
            );
          },
          sorter: (a: DataType, b: DataType): number =>
            stringCompare(a.parentCategoryUUID, b.parentCategoryUUID),
          width: '10%',
        };

    return [
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.name, b.name),
        render: (text: string, record: DataType): React.ReactElement => (
          <Link to={`/category/${record.uuid}`} label={text} openInNewTab />
        ),
        width: '50%',
        ...getColumnSearch('name'),
      },
      {
        title: 'Position',
        dataIndex: 'position',
        render: (text: number): number => text,
        sorter: (a: DataType, b: DataType): number => a.position - b.position,
        width: '10%',
      },
      {
        title: 'End price margin rate',
        dataIndex: 'endPriceMarginRate',
        sorter: (a: DataType, b: DataType): number =>
          numberCompare(a.endPriceMarginRate, b.endPriceMarginRate),
        render: (
          endPriceMarginRate: number | undefined,
        ): React.ReactElement => {
          const epmr: string = endPriceMarginRate
            ? `${round((endPriceMarginRate - 1) * 100, 2)}%`
            : EMPTY_VALUE;

          return (
            <Space orientation="vertical" align="start" size={SPACING.sm}>
              <Text>{epmr}</Text>
            </Space>
          );
        },
        filterSearch: false,
        filters: [
          { text: 'Margin rate below -10%', value: 'marginRateBelow10' },
          {
            text: 'Margin rate between -10% and 0%',
            value: 'marginRateBetween10And0',
          },
          {
            text: 'Margin rate between 0% and 10%',
            value: 'marginRateBetween0And10',
          },
          { text: 'Margin rate above 10%', value: 'marginRateAbove10' },
        ],
        onFilter: (value: boolean | Key, record: DataType): boolean => {
          if (value === 'marginRateBelow10') {
            return !!(
              record.endPriceMarginRate && record.endPriceMarginRate < 0.9
            );
          }
          if (value === 'marginRateBetween10And0') {
            return !!(
              record.endPriceMarginRate &&
              record.endPriceMarginRate >= 0.9 &&
              record.endPriceMarginRate < 1
            );
          }
          if (value === 'marginRateBetween0And10') {
            return !!(
              record.endPriceMarginRate &&
              record.endPriceMarginRate >= 1 &&
              record.endPriceMarginRate < 1.1
            );
          }
          if (value === 'marginRateAbove10') {
            return !!(
              record.endPriceMarginRate && record.endPriceMarginRate >= 1.1
            );
          }
          return false;
        },
        width: '10%',
      },
      parentOrChildColumn,
      {
        title: 'Comparison portal',
        dataIndex: 'comparisonPortalTypes',
        render: (
          comparisonPortalTypes: IComparisonPortal[],
        ): React.ReactElement[] =>
          comparisonPortalTypes.map(
            (comparisonPortalType: IComparisonPortal): React.ReactElement => (
              <Tag key={comparisonPortalType.uuid}>
                {comparisonPortalType.name}
              </Tag>
            ),
          ),
        sorter: (a: DataType, b: DataType): number =>
          a.comparisonPortalTypes.length - b.comparisonPortalTypes.length,
        width: '10%',
      },
      {
        title: 'Ebay',
        dataIndex: 'ebay',
        render: ({
          marketplaces,
        }: {
          marketplaces: IEbayMarketplace[];
        }): React.ReactElement[] =>
          marketplaces.map(
            ({ name, code }: IEbayMarketplace): React.ReactElement => (
              <Tag key={code}>{name}</Tag>
            ),
          ),
        sorter: (a: DataType, b: DataType): number =>
          a.ebay.marketplaces.length - b.ebay.marketplaces.length,
        width: '10%',
      },
      {
        title: 'Actions',
        render: (_text, record: DataType): React.ReactElement => (
          <ExternalLink
            to={`${configUrl}/${getLowerCaseLanguageCode(languageType)}/category/${record.uuid}`}
            ariaLabel="Open category in new tab"
          />
        ),
        width: '10%',
      },
    ];
  }, [
    dataSource,
    showOnlyParentCategories,
    categoriesAll,
    languageType,
    configUrl,
  ]);

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Button
          type={!categoriesAll ? 'primary' : 'default'}
          onClick={(): void => {
            dispatch(categoriesLoadAll());
          }}
          disabled={categoriesInProcess}
        >
          Reload all <ReloadOutlined />
        </Button>

        <Space>
          <Typography>Show only parent categories</Typography>

          <Switch
            checked={showOnlyParentCategories}
            onChange={(checked: boolean): void =>
              setShowOnlyParentCategories(checked)
            }
          />
        </Space>

        <UpdatedAt value={categoriesUpdatedAt} />
      </Space>

      <LanguageButtons />

      <Form form={form} onFinish={onFinish}>
        <Row gutter={[SPACING.none, SPACING.xl]}>
          <Col span={8} offset={8} className="gutter-row">
            <Form.Item
              name="name"
              rules={[
                {
                  required: true,
                },
                {
                  type: 'string',
                  min: 3,
                },
              ]}
            >
              <Input
                size="large"
                placeholder="Create new category name"
                prefix={<EditOutlined />}
                type="string"
                disabled={categoriesInProcess}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Collapse
        items={[
          {
            key: 'warehouseCategorySuggestions',
            label: 'AI suggestions for unpaired warehouse categories',
            children: <WarehouseCategorySuggestions />,
          },
        ]}
      />

      <Button
        type="primary"
        onClick={(): void => setIsModalOpen(true)}
        disabled={!showOnlyParentCategories}
      >
        Change categories order
      </Button>

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={categoriesInProcess && !categoriesAll}
        emptyText="No categories found"
      />

      <Modal
        title="Change categories order"
        open={isModalOpen}
        onCancel={(): void => setIsModalOpen(false)}
        footer={null}
      >
        <SortableList
          records={dataSourceSortableList}
          id="categories-sortable-list"
          ItemTemplate={ItemTemplate}
          saveCallback={(records: ISortableListItem[]): void => {
            records.forEach((record: ISortableListItem): void => {
              dispatch(categorySavePosition(record.uuid, record.position));
            });
            setIsModalOpen(false);
          }}
        />
      </Modal>
    </Space>
  );
}

export default CategoriesContainer;
